import React, { type RefCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import {
	ExpandButton,
	CreateChildButtonWithExpandToggle,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/expand-button/index.tsx';
import type { mergedCell_nativeIssueTable$key } from '@atlassian/jira-relay/src/__generated__/mergedCell_nativeIssueTable.graphql';
import {
	useIsChildIssueCreateEnabled,
	useIsGridNavigationEnabled,
} from '../../../../controllers/features/selectors.tsx';

const CELL_PADDING = 8;
const HIERARCHY_LEVEL_PADDING = 12;
const EXPAND_ICON_WIDTH = 24;

export type RenderContent = (args: { actionButtons: React.ReactNode }) => React.ReactNode;

export type Props = {
	issueRef: mergedCell_nativeIssueTable$key;
	itemsConnectionId: string;
	depth: number;
	isRowHoveredOrSelected?: boolean;
	isCreateChildFormVisible?: boolean;
	createChildTriggerProps?: {
		onClick: () => void;
		ref: RefCallback<HTMLElement>;
	};
	groupId?: string;
	renderContent: RenderContent;
	isHierarchyEnabled?: boolean;
};

export const MergedCell = ({
	issueRef,
	itemsConnectionId,
	depth,
	isRowHoveredOrSelected,
	createChildTriggerProps,
	isCreateChildFormVisible,
	groupId,
	renderContent,
	isHierarchyEnabled,
}: Props) => {
	const issueData = useFragment<mergedCell_nativeIssueTable$key>(
		graphql`
			fragment mergedCell_nativeIssueTable on JiraIssueEdge
			@argumentDefinitions(
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				shouldQueryHasChildren: { type: "Boolean!" }
				isJscM2Enabled: { type: "Boolean!" }
			) {
				hasChildren(filterByProjectKeys: $projectKeys)
					@include(if: $shouldQueryHasChildren)
					@optIn(to: "JiraSpreadsheetComponent-M2")
				canHaveChildIssues(projectKey: $projectKey)
					@include(if: $shouldQueryHasChildren)
					@optIn(to: "JiraSpreadsheetComponent-M2")
				node @include(if: $isJscM2Enabled) {
					issueId
				}
			}
		`,
		issueRef,
	);

	const hasChildren = Boolean(issueData?.hasChildren);
	const canHaveChildIssues = Boolean(issueData?.canHaveChildIssues);
	const issueId = issueData?.node?.issueId;

	const isChildIssueCreateEnabled = useIsChildIssueCreateEnabled();
	const isGridNavigationEnabled = useIsGridNavigationEnabled();
	const showCreateChildButton =
		isChildIssueCreateEnabled && isRowHoveredOrSelected && canHaveChildIssues;

	const childCreateButton = showCreateChildButton && issueId && (
		<CreateChildButtonWithExpandToggle
			depth={depth}
			itemId={issueId}
			groupId={groupId}
			itemsConnectionId={itemsConnectionId}
			createChildTriggerProps={createChildTriggerProps}
			isCreateChildFormVisible={isCreateChildFormVisible}
		/>
	);

	// TODO: JSC-1774 Render contextual menu button here
	const actionButtons = <>{childCreateButton}</>;

	if (hasChildren && issueId) {
		return (
			<Box
				xcss={cellStyles}
				tabIndex={isGridNavigationEnabled ? undefined : 0}
				// eslint-disable-next-line jira/react/no-style-attribute
				style={{
					paddingLeft: `${HIERARCHY_LEVEL_PADDING * depth + CELL_PADDING}px`,
				}}
			>
				<Box xcss={expandButtonStyle}>
					<ExpandButton itemId={issueId} itemsConnectionId={itemsConnectionId} depth={depth} />
				</Box>
				{renderContent({ actionButtons })}
			</Box>
		);
	}

	const cellPadding = isHierarchyEnabled
		? EXPAND_ICON_WIDTH + HIERARCHY_LEVEL_PADDING * depth + CELL_PADDING
		: 0;

	return (
		<Box
			xcss={cellStyles}
			tabIndex={isGridNavigationEnabled ? undefined : 0}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				paddingLeft: `${cellPadding}px`,
			}}
		>
			{renderContent({ actionButtons })}
		</Box>
	);
};

const cellStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	height: ' 100%',
	position: 'relative',
});

const expandButtonStyle = xcss({
	display: 'flex',
	alignItems: 'center',
});
